/**
 * CoachingUX launcher factory for Web SDK
 */

import * as Api from "@ms-ofb/officefloodgatecore/dist/src/Api/Api";

import * as Configuration from "./../Configuration/Configuration";
import { IInitOptionsCommon } from "./../Configuration/Configuration";
import { getDynamicSetting, SettingKey } from "../Configuration/DynamicConfiguration";

import * as Constants from "../Constants";
import * as DomUtils from "../Window/DomUtils";
import * as Window from "../Window/Window";
import * as Logging from "../Logging/Logging";
import * as AssetsProvider from "../TargetedMessaging/AssetsProvider";
import { isNOU } from "../Utils";

import { ISurveyParams } from "@ms-ofb/officefloodgatecore/dist/src/Api/IOnSurveyActivatedCallback";

export interface IBeaconParameters {
	actionName: string;
	actionUrl: string;
}

export interface IOfficeWebSurfaces {
	Initialize(intOptions: IInitOptionsCommon): void;
	RenderSurface(
		surfaceParams: any,
		targetElementId?: string,
		surfaceId?: string): void;
	RemoveSurface(): void;
	GetSurfaceContainerId(): string;
}

declare module "../Window/Window" {
	interface IWindow {
		OfficeWebSurfaces: IOfficeWebSurfaces;
	}
}

export function getWebSurfacesLink() {
	const internalEnvs = ["dogfood", "edog", "int", "microsoft"];
	const commonInitOptions = Configuration.get().getCommonInitOptions();
	const audience = commonInitOptions.telemetryGroup?.audienceGroup || commonInitOptions.telemetryGroup?.audience;
	const isInternalEnv = audience && internalEnvs.indexOf(audience.toLowerCase()) !== -1;
	const webSurfacesConfigLink = isInternalEnv ?
		commonInitOptions.webSurfacesSettings?.webSurfacesIntUrl : commonInitOptions.webSurfacesSettings?.webSurfacesProdUrl;
	const webSurfacesDefaultLink = isInternalEnv ?
		Constants.DynamicScriptUrls.WebSurfacesIntLink : Constants.DynamicScriptUrls.WebSurfacesProdLink;
	const settingKey = isInternalEnv ? SettingKey.webSurfacesIntLink : SettingKey.webSurfacesProdLink;
	const webSurfacesLink = webSurfacesConfigLink || getDynamicSetting(settingKey, webSurfacesDefaultLink);
	return webSurfacesLink;
}

// web surfaces script url load promise
let WebSurfacesScriptPromise: Promise<void>;
function createwebSurfacesScriptPromise() {
	if (!WebSurfacesScriptPromise) {
		WebSurfacesScriptPromise = DomUtils.loadScript(getWebSurfacesLink(), null);
	}
}

export default class CoachingUXLauncherFactory implements Api.ISurveyLauncherFactory {
	public static readonly LAUNCHER_TYPE: string = "coachingux";

	public makeSurveyLauncher(survey: Api.ISurvey): Api.ISurveyLauncher {
		if (this.AcceptsSurvey(survey)) {
			return new CoachingUXLauncher(survey);
		}

		return null;
	}

	public AcceptsSurvey(survey: Api.ISurvey): boolean {
		if (!isNOU(survey) && survey.getType() === Api.ISurvey.Type.GenericMessagingSurface
			&& survey.getLauncherType().toLocaleLowerCase() === CoachingUXLauncherFactory.LAUNCHER_TYPE) {
			return true;
		}

		return false;
	}
}

export class CoachingUXLauncher implements Api.ISurveyLauncher {
	private survey: Api.ISurvey;

	constructor(survey: Api.ISurvey) {
		this.survey = survey;

		// pre-load web surface package if there are any critical campaigns
		if (!WebSurfacesScriptPromise &&
			survey && survey.getSurveyInfo &&
			survey.getSurveyInfo()?.getBusinessLevel() === Api.ISurveyInfo.BusinessLevel.Critical) {
			setTimeout(createwebSurfacesScriptPromise, 1000);
		}
	}

	public launch(surveyParams?: ISurveyParams): void {
		const surveyInfo = this.survey && this.survey.getSurveyInfo();
		const surveyMetadata = surveyInfo && surveyInfo.getMetadata();
		const contentMetadata = surveyMetadata && surveyMetadata.getContentMetadata();
		if (!contentMetadata) {
			return;
		}

		const startTime = new Date();
		const getWebSurfacesInstance = () => {
			const windowInstance = Window.getGlobal();
			return windowInstance && windowInstance.OfficeWebSurfaces;
		};

		const renderWebSurface = (uxInitOptions?: any) => {
			const officeWebSurfaces = getWebSurfacesInstance();
			if (uxInitOptions) {
				officeWebSurfaces.Initialize(uxInitOptions);
			}

			officeWebSurfaces.RemoveSurface();

			const surfaceParams = {
				...contentMetadata,
				surfaceContext: {
					surveyForm: this.survey,
					surveyParams,
				},
			};
			officeWebSurfaces.RenderSurface(
				surfaceParams,
				null /* targetElementId */,
				surveyInfo.getBackEndId());
		};

		const logErrorEvent = (error?: Error) => {
			Logging.getLogger().logEvent(
				Logging.EventIds.WebSurfaces.Common.Error.VALUE,
				Logging.LogLevel.Critical,
				{
					ErrorMessage: "Error loading websurfaces" + (error && error.message ? ": " + error.message : ""),
					TimeMilliseconds: Date.now() - (+startTime),
				});
		};

		try {
			if (getWebSurfacesInstance()) {
				renderWebSurface();
				return;
			}

			createwebSurfacesScriptPromise();

			// if UX package isn't already loaded, download package and render surface
			WebSurfacesScriptPromise
			.then(() => {
				const initOptions = Configuration.get().getCommonInitOptions();
				const floodgateInitOptions = Configuration.get().getFloodgateInitOptions();
				const surveyActivatedCallback = floodgateInitOptions && floodgateInitOptions.onSurveyActivatedCallback;

				const beaconCallback = (beaconParameters: IBeaconParameters): Promise<boolean> => {
					if (beaconParameters) {
						return AssetsProvider.get().setCampaignAction(beaconParameters);
					}
				};

				const uxOptions = {
					...initOptions,
					...floodgateInitOptions,
					beaconCallback,
					surfaceInitTime: startTime,
					surfaceId: surveyInfo.getBackEndId(),
					logSessionId: Configuration.get().getSdkSessionId(),
					logSettings: getDynamicSetting(SettingKey.logLevelSettings, undefined),
					renderer: surveyActivatedCallback,
					renderCallback: surveyActivatedCallback && surveyActivatedCallback.onTeachingCampaignRender,
				};

				renderWebSurface(uxOptions);
			}).catch(error => {
				logErrorEvent(error);
			});
		} catch (error) {
			logErrorEvent(error);
		}
	}
}
